import { MsalProvider } from "@azure/msal-react";
import { CssBaseline } from "@mui/material";
import { LicenseInfo } from "@mui/x-license-pro";
import { SnackbarDefault } from "components";
import { NotificationDefault } from "components/Notification";
import { MSALGlobal, MUILicenseKey, QueryClientGlobal } from "configs";
import {
  AppNavigationProvider,
  DialogServiceProvider,
  MessageServiceProvider,
  UseAlert,
  UseConfirm,
  UseMessage,
  message,
} from "contexts";
import AppThemeProvider from "contexts/Theme/provider";
import { AppInitializer, AzureInitializer } from "libs";
import { SnackbarProvider } from "notistack";
import { EmployeeErrorDialog } from "pages/Components";
import { ErrorBoundary } from "react-error-boundary";
import { QueryClient, QueryClientProvider } from "react-query";
import { ReactQueryDevtools } from "react-query/devtools";
import { Provider as ReduxProvider } from "react-redux";
import { BrowserRouter } from "react-router-dom";
import { PersistGate } from "redux-persist/integration/react";
import { AppRoutes } from "routes";
import store, { persistor } from "stores";
import { i18n } from "translations";

import "./index.scss";

if (process.env.NODE_ENV === "production" && MUILicenseKey) {
  LicenseInfo.setLicenseKey(MUILicenseKey);
}

const onErrorHandler = () => {
  message({
    title: i18n.t("aNewVersionOfTheApplicationIsAvailable"),
    content: i18n.t("pleaseReloadAndTryAgain"),
    buttonLabel: i18n.t("reload"),
    disableCloseOnBackdropAndEscape: true,
    closeable: false,
    callback: () => window.location.reload(),
  });
};

function App() {
  return (
    <QueryClientProvider client={QueryClientGlobal as QueryClient}>
      <ReduxProvider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <MsalProvider instance={MSALGlobal}>
            <AppThemeProvider showModeTool={false}>
              <BrowserRouter>
                <AppNavigationProvider>
                  <DialogServiceProvider>
                    <MessageServiceProvider>
                      <ErrorBoundary
                        fallbackRender={() => null}
                        onError={onErrorHandler}
                      >
                        <SnackbarProvider
                          maxSnack={3}
                          autoHideDuration={3000}
                          Components={{
                            default: SnackbarDefault,
                            success: NotificationDefault,
                            error: NotificationDefault,
                            warning: NotificationDefault,
                            info: NotificationDefault,
                          }}
                        >
                          <CssBaseline />
                          <ReactQueryDevtools />
                          <AppInitializer />
                          <AzureInitializer />
                          <UseConfirm />
                          <UseAlert />
                          <UseMessage />
                          <AppRoutes />
                          <EmployeeErrorDialog />
                        </SnackbarProvider>
                      </ErrorBoundary>
                    </MessageServiceProvider>
                  </DialogServiceProvider>
                </AppNavigationProvider>
              </BrowserRouter>
            </AppThemeProvider>
          </MsalProvider>
        </PersistGate>
      </ReduxProvider>
    </QueryClientProvider>
  );
}

export default App;
